import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { EmploymentInfoTab } from '@client/components/users/user-detail/EmploymentInfoTab';
import { toUserFormValues } from '@client/components/users/user-detail/form-values';
import { NotificationsTab } from '@client/components/users/user-detail/NotificationsTab';
import { PermissionsTab } from '@client/components/users/user-detail/PermissionsTab';
import { PersonalInfoTab } from '@client/components/users/user-detail/PersonalInfoTab';
import { isTabKey, resolveTabForField, type TabKey } from '@client/components/users/user-detail/tab-fields';
import type { UserInput } from '@client/components/users/user-detail/types';
import { getUserDisplayName, UserDetailHeader } from '@client/components/users/user-detail/UserDetailHeader';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgById } from '@client/hooks/business/org/useOrgById';
import { useUnsavedChangesPrompt } from '@client/hooks/forms/useUnsavedChangesPrompt';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { AUTH0_CONNECTIONS, CORE_SYSTEM_ROLES, ORG_ROLES, USER_SYSTEM_USAGE_KEYS } from '@shared/constants';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Skeleton, Switch, Tabs } from 'antd';
import { Bell, Briefcase, Headphones, ShieldCheck, User } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

export const Route = createFileRoute('/admin-console/organisation-management/organisations/$orgId/users/edit/$userId')({
    params: {
        parse({ userId }) {
            return {
                userId: BigInt(userId),
            };
        },
    },
    staticData: {
        presentation: 'fullPage',
        ui: {
            widthVariant: 'default',
            hideTitle: true,
        },
    },
    beforeLoad(ctx): ShareableRouteContext {
        // Dynamic breadcrumb: the user's name (getUserDisplayName falls back to email when unnamed).
        async function breadcrumb() {
            const user = await ctx.context.trpcUtils.user.getUser.ensureData({
                id: ctx.params.userId,
                orgId: ctx.params.orgId,
                include: ['handlingPartyIds', 'trainingManagerHandlingPartyIds'],
            });
            return getUserDisplayName(user);
        }

        return {
            breadcrumb: { title: breadcrumb },
            // Replace the org route's inherited page header ("<org name> / Manage organisation
            // settings and users") — the user detail header card is this page's heading.
            ui: { title: '' },
        };
    },
    component: EditUserRoute,
});

function EditUserRoute() {
    return (
        <PageController route={Route}>
            <AdminUserDetail />
        </PageController>
    );
}

const USERS_LIST_ROUTE = '/admin-console/organisation-management/organisations/$orgId/users';

function AdminUserDetail() {
    const { orgId, userId } = Route.useParams();
    const { org } = useOrgById(orgId);
    const { isReadOnly } = useAdminModulePermissions('users');
    const navigate = useNavigate();
    const { trpcClient, trpcUtils } = useTrpc();
    const { fDateShortDayJs, formatDate } = useFormatter();
    const formId = useId();
    const [form] = Form.useForm<UserInput>();
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const isDirtyRef = useRef(false);
    const hydratedUserIdRef = useRef<bigint | null>(null);
    useUnsavedChangesPrompt(isDirtyRef);

    const markDirty = () => {
        isDirtyRef.current = true;
        setIsDirty(true);
    };
    const markClean = () => {
        isDirtyRef.current = false;
        setIsDirty(false);
    };
    const [activeTab, setActiveTab] = useState<TabKey>('personal_info');

    const goToList = () => navigate({ to: USERS_LIST_ROUTE, params: { orgId } });

    const { data: user, isLoading } = trpc.user.getUser.useQuery(
        { id: userId, orgId, include: ['handlingPartyIds', 'trainingManagerHandlingPartyIds'] },
        {
            refetchOnWindowFocus: false,
            throwOnError(error) {
                errorMessage.show(error);
                markClean();
                goToList();
                return false;
            },
        },
    );
    const { data: supportSessionStatus } = trpc.supportSession.getSessionForUser.useQuery(
        user?.isSupportAccount ? { userId, orgId } : skipToken,
        { refetchOnWindowFocus: false },
    );
    const { data: trainingPeriodInfo } = trpc.trainingPeriod.getUserCurrentTrainingPeriodInfo.useQuery(
        { userId, orgId },
        { refetchOnWindowFocus: false },
    );

    const { mutate: updateUser } = trpc.admin.user.updateOrgUser.useMutation();
    const { mutate: resendUserInvite } = trpc.admin.user.resendUserInvite.useMutation();
    const { mutate: convertFromSupport, isPending: isConverting } = trpc.user.convertFromSupportAccount.useMutation({
        onSuccess() {
            message.success('User converted to normal account');
            void trpcUtils.user.getUser.invalidate({ id: userId, orgId });
            void trpcUtils.user.getUserForDisplay.invalidate({ id: userId, orgId });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });
    const { mutate: setSupportAccount, isPending: isSettingSupportAccount } =
        trpc.admin.adminRole.setSupportAccount.useMutation({
            onSuccess() {
                message.success('Support account status updated.');
                void trpcUtils.user.getUser.invalidate({ id: userId, orgId });
                void trpcUtils.user.getUserForDisplay.invalidate({ id: userId, orgId });
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    useCustomDocumentTitle(user ? getUserDisplayName(user) : 'User');

    const isEmployee = Form.useWatch(({ isEmployee }) => isEmployee, form);
    const roles = Form.useWatch(({ roles }) => roles, form) || [];
    const disabled = Form.useWatch(({ disabled }) => disabled, form);

    const isTrainingManagerOnly =
        roles.includes(ORG_ROLES.TRAINING_MANAGER) &&
        !roles.some((role) => (CORE_SYSTEM_ROLES as readonly string[]).includes(role));

    useEffect(() => {
        if (!user) {
            return;
        }

        const isDifferentUser = hydratedUserIdRef.current !== userId;
        if (!isDifferentUser && isDirtyRef.current) {
            return;
        }

        form.setFieldsValue(toUserFormValues(user));
        hydratedUserIdRef.current = userId;
    }, [user, userId, form]);

    useEffect(() => {
        if (isTrainingManagerOnly) {
            form.setFieldValue('systemUsage', USER_SYSTEM_USAGE_KEYS.non_contributory);
        }
    }, [isTrainingManagerOnly, form]);

    function onSendInvite() {
        message.loading('Sending invite...');
        resendUserInvite(
            { orgId, userId },
            {
                onSuccess() {
                    message.success('Invite sent successfully.');
                },
                onError(error) {
                    if (error.data?.errorId === 'invite.emailSendFailed') {
                        errorMessage.show(error.message);
                        return;
                    }
                    errorMessage.show(error);
                },
            },
        );
    }

    async function onResetMfa() {
        if (user?.disabled) {
            errorMessage.show('Cannot reset MFA for disabled users.', 'Reset MFA');
            return;
        }
        try {
            message.loading('Resetting MFA...');
            await trpcClient.user.resetMfa.mutate({ orgId, userId });
            message.success('MFA has been reset successfully.');
        } catch (e) {
            message.destroy();
            errorMessage.show(e);
        }
    }

    async function onResetPassword() {
        invariant(user, 'user is required');
        if (user.disabled) {
            errorMessage.show('Cannot send reset password email to disabled users.', 'Password reset');
            return;
        }
        if (user.provider !== AUTH0_CONNECTIONS.DATABASE.name) {
            errorMessage.show(
                `Cannot send reset password email to users from ${user.provider} identity provider.`,
                'Password reset',
            );
            return;
        }
        try {
            message.loading('Sending reset password e-mail...');
            await trpcClient.user.resetPassword.mutate({ orgId, userId });
            message.success('Reset password e-mail was sent.');
        } catch (e) {
            message.destroy();
            errorMessage.show(e);
        }
    }

    function onRevokeAccess() {
        if (!user) {
            return;
        }
        confirm({
            title: 'Revoke access',
            content: `Are you sure you want to revoke access for ${getUserDisplayName(user)}? They will no longer be able to log in to this organisation.`,
            okText: 'Revoke access',
            okDanger: true,
            async onOk() {
                try {
                    await trpcClient.admin.user.updateOrgUser.mutate({ userId, orgId, set: { disabled: true } });
                    message.success('Access revoked successfully');
                    void trpcUtils.admin.user.listOrgUsers.invalidate();
                    markClean();
                    goToList();
                } catch (e) {
                    errorMessage.show(e);
                }
            },
        });
    }

    function onConvertToNormalUser() {
        confirm({
            title: 'Convert to normal user?',
            content:
                'This will remove the support account status and allow you to edit this user. They will retain their current roles.',
            onOk() {
                convertFromSupport({ userId, orgId });
            },
        });
    }

    if (isLoading || !user) {
        return (
            <div className="rounded-lg border border-separator bg-card p-6">
                <Skeleton active />
            </div>
        );
    }

    const lastLoginLabel = user.lastLoginAt ? formatDate(user.lastLoginAt, 'date-time') : 'Never logged in';

    // Admin-console-only controls above the role selector: the admin-support-role toggle for
    // admin users, or an informational note when the role was assigned elsewhere.
    const adminSupportControls = user.isAdmin ? (
        <div className="mb-4 flex max-w-160 items-center justify-between gap-4 rounded-lg border border-separator bg-card p-4">
            <div className="min-w-0">
                <div className="font-semibold text-foreground text-sm">Support account</div>
                <div className="text-muted-foreground text-xs">
                    Support accounts can access organisations via approved support sessions.
                </div>
            </div>
            <Switch
                checked={user.hasAdminSupportRole}
                onChange={(checked) => setSupportAccount({ userId, isSupportAccount: checked })}
                loading={isSettingSupportAccount}
                disabled={isReadOnly}
            />
        </div>
    ) : user.hasAdminSupportRole ? (
        <div className="mb-4 flex max-w-200 items-center gap-3 rounded-lg border border-info-border bg-info-subtle p-4">
            <Headphones className="size-4 shrink-0 text-info-foreground" />
            <p className="mb-0 text-muted-foreground text-sm">
                This user is a support account (has the admin support role in the admin console).
            </p>
        </div>
    ) : null;

    return (
        <div className="pb-24">
            <ReadOnlyBanner module="users" />

            <UserDetailHeader
                user={user}
                lastLoginLabel={lastLoginLabel}
                onSendInvite={onSendInvite}
                onResetMfa={onResetMfa}
                onResetPassword={onResetPassword}
                onRevokeAccess={onRevokeAccess}
                backLink={
                    <BackLink to={USERS_LIST_ROUTE} params={{ orgId }}>
                        Back to users
                    </BackLink>
                }
                readOnly={isReadOnly}
            />

            <Form
                id={formId}
                form={form}
                layout="vertical"
                className="mt-4"
                disabled={isReadOnly}
                onValuesChange={markDirty}
                onFinishFailed={({ errorFields }) => {
                    // Validation can fail on a tab the user isn't looking at (all tabs force-render so
                    // every field submits). Surface it: jump to the first erroring tab and flag it.
                    const firstField = errorFields[0]?.name?.[0];
                    if (firstField != null) {
                        setActiveTab(resolveTabForField(String(firstField)));
                        requestAnimationFrame(() => form.scrollToField(errorFields[0].name, { block: 'center' }));
                    }
                    message.error('Please fix the highlighted fields before saving.');
                }}
                onFinish={(values) => {
                    setIsSaving(true);
                    message.loading('Saving...');

                    updateUser(
                        {
                            userId,
                            orgId,
                            set: {
                                ...values,
                                // NumericInput emits undefined when cleared; send null so the override is removed
                                pointsTargetOverride: values.pointsTargetOverride ?? null,
                            },
                        },
                        {
                            onSuccess() {
                                message.success('Saved.');
                                setIsSaving(false);
                                markClean();
                                void trpcUtils.user.getUser.invalidate({ id: userId, orgId });
                                void trpcUtils.user.getUserForDisplay.invalidate({ id: userId, orgId });
                                void trpcUtils.admin.user.listOrgUsers.invalidate();
                            },
                            onError(error) {
                                setIsSaving(false);
                                if (error.data?.errorId === 'user.phoneAlreadyTaken') {
                                    message.destroy();
                                    const phoneError = 'This phone number is already in use.';
                                    form.setFields([{ name: 'phone', errors: [phoneError] }]);
                                    // The phone field lives on the Personal tab; this server-side conflict is
                                    // invisible when the user saved from another tab, so jump to it and announce it.
                                    setActiveTab(resolveTabForField('phone'));
                                    requestAnimationFrame(() => form.scrollToField('phone', { block: 'center' }));
                                    message.error(phoneError);
                                    return;
                                }
                                errorMessage.show(error);
                            },
                        },
                    );
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        if (isTabKey(key)) {
                            setActiveTab(key);
                        }
                    }}
                    items={[
                        {
                            key: 'personal_info',
                            label: (
                                <span className="flex items-center gap-2">
                                    <User className="size-3.5" />
                                    Personal information
                                </span>
                            ),
                            forceRender: true,
                            children: <PersonalInfoTab user={user} />,
                        },
                        {
                            key: 'employment_info',
                            label: (
                                <span className="flex items-center gap-2">
                                    <Briefcase className="size-3.5" />
                                    Employment information
                                </span>
                            ),
                            forceRender: true,
                            children: (
                                <EmploymentInfoTab
                                    form={form}
                                    isEmployee={isEmployee}
                                    isTrainingManagerOnly={isTrainingManagerOnly}
                                    trainingPeriodInfo={trainingPeriodInfo}
                                    dateFormat={fDateShortDayJs}
                                />
                            ),
                        },
                        {
                            key: 'notifications',
                            label: (
                                <span className="flex items-center gap-2">
                                    <Bell className="size-3.5" />
                                    Notifications
                                </span>
                            ),
                            forceRender: true,
                            children: (
                                <NotificationsTab
                                    form={form}
                                    onDirty={markDirty}
                                    enabledModules={org?.enabledModules ?? []}
                                />
                            ),
                        },
                        {
                            key: 'permissions',
                            label: (
                                <span className="flex items-center gap-2">
                                    <ShieldCheck className="size-3.5" />
                                    Permissions
                                </span>
                            ),
                            forceRender: true,
                            children: (
                                <PermissionsTab
                                    user={user}
                                    orgId={orgId}
                                    enabledModules={org?.enabledModules ?? []}
                                    // The admin console is always operated by super admins.
                                    isAdmin
                                    canModifyRoles={!isReadOnly}
                                    roles={roles}
                                    disabled={disabled}
                                    supportSessionStatus={supportSessionStatus}
                                    onConvertToNormalUser={onConvertToNormalUser}
                                    isConverting={isConverting}
                                    formatDate={formatDate}
                                    activeSupportSessionAction={
                                        <p className="mb-0 text-muted-foreground text-sm">
                                            Revoke the session first to convert this user.
                                        </p>
                                    }
                                    rolesPrefix={adminSupportControls}
                                    showTermsAccepted
                                />
                            ),
                        },
                    ]}
                />
            </Form>

            <div className="sticky bottom-0 z-10 mt-6 flex items-center justify-between gap-4 rounded-lg border border-separator bg-card px-5 py-3 shadow-lg">
                <span className="flex items-center gap-2 text-muted-foreground text-xs">
                    {isReadOnly ? (
                        'Read-only access'
                    ) : isDirty ? (
                        <>
                            <span className="size-1.5 rounded-full bg-warning" />
                            Unsaved changes
                        </>
                    ) : (
                        'All changes saved'
                    )}
                </span>
                <div className="flex gap-2.5">
                    <Button onClick={goToList}>{isReadOnly ? 'Close' : 'Cancel'}</Button>
                    {!isReadOnly && (
                        <Button type="primary" htmlType="submit" form={formId} loading={isSaving}>
                            Save changes
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
